import { apiFunctionsEnum } from 'constants/api.constants';
import ApiService from './api.service';
import {
	setHouseBalances,
	setError,
	setLoading,
	setDashboardDisplayedCurrencies,
	setAccountBalance,
	setPendingBalance,
	setPaymentFees,
	setConversionFees,
	setPlanFees,
	setAverageIncomeFromCustomer,
	setBeneficiaryDashboardList,
	setBeneficiaryResponseTimeSummary,
	setBeneficiaryStatusSummary,
	beginBeneficiaryDashboardListRequest,
	setBeneficiaryDashboardListLoading,
	setBeneficiaryResponseTimeSummaryLoading,
	setBeneficiaryStatusSummaryLoading,
} from 'state/reducers/balance.reducer';
import { setErrorMessage } from 'utils/index';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { IFilterType } from 'constants/balance.constants';
import _ from 'lodash';
import { setSuccessMessage } from 'state/reducers/ui.reducers';
import { getCompanyPaymentById } from './customer-accounts.service';
import { format } from 'date-fns';
import type { IBeneficiaryDashboardDateRange } from 'types/beneficiary-dashboard.types';

const toBeneficiaryDashboardDatePayload = (datesRange: IBeneficiaryDashboardDateRange) => ({
	dateFrom: format(datesRange.startDate, 'yyyy-MM-dd'),
	dateTo: format(datesRange.endDate, 'yyyy-MM-dd'),
});

export const getBeneficiaryStatusSummary = (datesRange: IBeneficiaryDashboardDateRange) => {
	return async (dispatch: any, getState: any) => {
		const requestId = getState().balance.beneficiaryCubeRequestId;
		try {
			const response = await ApiService.getInstance().execute(
				apiFunctionsEnum.GET_BENEFICIARY_STATUS_SUMMARY,
				toBeneficiaryDashboardDatePayload(datesRange),
			);
			if (getState().balance.beneficiaryCubeRequestId !== requestId) return;
			dispatch(setBeneficiaryStatusSummary(response.data.beneficiaryStatusSummary));
		} catch (error: any) {
			if (getState().balance.beneficiaryCubeRequestId !== requestId) return;
			setErrorMessage(setError, {
				errorNumber: error?.response?.data?.error_number,
				statusCode: error?.response?.status,
			});
		} finally {
			if (getState().balance.beneficiaryCubeRequestId === requestId) {
				dispatch(setBeneficiaryStatusSummaryLoading(false));
			}
		}
	};
};

export const getBeneficiaryResponseTimeSummary = (datesRange: IBeneficiaryDashboardDateRange) => {
	return async (dispatch: any, getState: any) => {
		const requestId = getState().balance.beneficiaryCubeRequestId;
		try {
			const response = await ApiService.getInstance().execute(
				apiFunctionsEnum.GET_BENEFICIARY_RESPONSE_TIME_SUMMARY,
				toBeneficiaryDashboardDatePayload(datesRange),
			);
			if (getState().balance.beneficiaryCubeRequestId !== requestId) return;
			dispatch(setBeneficiaryResponseTimeSummary(response.data.beneficiaryResponseTimeSummary));
		} catch (error: any) {
			if (getState().balance.beneficiaryCubeRequestId !== requestId) return;
			setErrorMessage(setError, {
				errorNumber: error?.response?.data?.error_number,
				statusCode: error?.response?.status,
			});
		} finally {
			if (getState().balance.beneficiaryCubeRequestId === requestId) {
				dispatch(setBeneficiaryResponseTimeSummaryLoading(false));
			}
		}
	};
};

export const getBeneficiaryDashboardBeneficiaries = (
	datesRange: IBeneficiaryDashboardDateRange,
	params: { offset: number; limit: number },
) => {
	return async (dispatch: any, getState: any) => {
		dispatch(beginBeneficiaryDashboardListRequest());
		const requestId = getState().balance.beneficiaryDashboardListRequestId;
		try {
			const response = await ApiService.getInstance().execute(
				apiFunctionsEnum.GET_BENEFICIARY_DASHBOARD_BENEFICIARIES,
				{
					...params,
					...toBeneficiaryDashboardDatePayload(datesRange),
				},
			);
			if (getState().balance.beneficiaryDashboardListRequestId !== requestId) return;
			dispatch(
				setBeneficiaryDashboardList({
					rows: response.data.result,
					total: response.data.pagination.total,
				}),
			);
		} catch (error: any) {
			if (getState().balance.beneficiaryDashboardListRequestId !== requestId) return;
			setErrorMessage(setError, {
				errorNumber: error?.response?.data?.error_number,
				statusCode: error?.response?.status,
			});
		} finally {
			if (getState().balance.beneficiaryDashboardListRequestId === requestId) {
				dispatch(setBeneficiaryDashboardListLoading(false));
			}
		}
	};
};
export function getHouseAccountBalances() {
	return async (dispatch: any) => {
		dispatch(setLoading(true));
		await ApiService.getInstance()
			.execute(apiFunctionsEnum.GET_HOUSE_ACCOUNT_BALANCES, {})
			.then((response: any) => {
				dispatch(setHouseBalances(response.data.houseAccountBalance));
			})
			.catch((error: any) => {
				setErrorMessage(setError, {
					errorNumber: error?.response?.data?.error_number,
					statusCode: error?.response?.status,
				});
			})
			.finally(() => {
				dispatch(setLoading(false));
			});
	};
}

export function getPlanFees(filters: IFilterType, isDetailed: boolean) {
	return async (dispatch: any) => {
		dispatch(setLoading(true));
		await ApiService.getInstance()
			.execute(apiFunctionsEnum.GET_PLAN_FEES, { filters, isDetailed })
			.then((response: any) => {
				dispatch(setPlanFees(response?.data?.monthlySubscriptions));
				return response.data;
			})
			.catch((error: any) => {
				setErrorMessage(setError, {
					errorNumber: error?.response?.data?.error_number,
					statusCode: error?.response?.status,
				});
			})
			.finally(() => {
				dispatch(setLoading(false));
			});
	};
}

export function getPlanFeesByCompany(filters: IFilterType, isDetailed: boolean) {
	return async (dispatch: any) => {
		dispatch(setLoading(true));
		await ApiService.getInstance()
			.execute(apiFunctionsEnum.GET_PLAN_FEES_BY_COMPANY, { filters, isDetailed })
			.then((response: any) => {
				dispatch(setPlanFees(response?.data?.monthlySubscriptions));
				return response.data;
			})
			.catch((error: any) => {
				setErrorMessage(setError, {
					errorNumber: error?.response?.data?.error_number,
					statusCode: error?.response?.status,
				});
			})
			.finally(() => {
				dispatch(setLoading(false));
			});
	};
}

export function getAccountBalances(filters: IFilterType, isDetailed: boolean) {
	return async (dispatch: any) => {
		dispatch(setLoading(true));
		await ApiService.getInstance()
			.execute(apiFunctionsEnum.GET_ACCOUNT_BALANCE, { filters, isDetailed })
			.then((response: any) => {
				dispatch(setAccountBalance(response.data.accountBalance));
			})
			.catch((error: any) => {
				setErrorMessage(setError, {
					errorNumber: error?.response?.data?.error_number,
					statusCode: error?.response?.status,
				});
			})
			.finally(() => {
				dispatch(setLoading(false));
			});
	};
}

export function getPendingBalances(filters: IFilterType, isDetailed: boolean) {
	return async (dispatch: any) => {
		dispatch(setLoading(true));
		await ApiService.getInstance()
			.execute(apiFunctionsEnum.GET_PENDING_BALANCE, { filters, isDetailed })
			.then((response: any) => {
				dispatch(setPendingBalance(response.data.pendingBalance));
			})
			.catch((error: any) => {
				setErrorMessage(setError, {
					errorNumber: error?.response?.data?.error_number,
					statusCode: error?.response?.status,
				});
			})
			.finally(() => {
				dispatch(setLoading(false));
			});
	};
}

export function getPaymentFees(filters: IFilterType, isDetailed: boolean) {
	return async (dispatch: any) => {
		dispatch(setLoading(true));
		await ApiService.getInstance()
			.execute(apiFunctionsEnum.GET_PAYMENT_FEES, { filters, isDetailed })
			.then((response: any) => {
				dispatch(setPaymentFees(_.get(response.data, 'paymentFees[0]')));
			})
			.catch((error: any) => {
				setErrorMessage(setError, {
					errorNumber: error?.response?.data?.error_number,
					statusCode: error?.response?.status,
				});
			})
			.finally(() => {
				dispatch(setLoading(false));
			});
	};
}

export function getConversionFees(filters: IFilterType, isDetailed: boolean) {
	return async (dispatch: any) => {
		dispatch(setLoading(true));
		await ApiService.getInstance()
			.execute(apiFunctionsEnum.GET_CONVERSION_FEES, { filters, isDetailed })
			.then((response: any) => {
				dispatch(setConversionFees(_.get(response.data, 'conversionFees[0]')));
			})
			.catch((error: any) => {
				setErrorMessage(setError, {
					errorNumber: error?.response?.data?.error_number,
					statusCode: error?.response?.status,
				});
			})
			.finally(() => {
				dispatch(setLoading(false));
			});
	};
}

export function getAverageIncomeFromCustomer(filters: IFilterType) {
	return async (dispatch: any) => {
		dispatch(setLoading(true));
		await ApiService.getInstance()
			.execute(apiFunctionsEnum.GET_AVERAGE_INCOME_FROM_CUSTOMER, { filters })
			.then((response: any) => {
				dispatch(
					setAverageIncomeFromCustomer(
						_.get(response.data, 'totalRevenueByCompany[0].averageIncomeFromCustomer')
					)
				);
			})
			.catch((error: any) => {
				setErrorMessage(setError, {
					errorNumber: error?.response?.data?.error_number,
					statusCode: error?.response?.status,
				});
			})
			.finally(() => {
				dispatch(setLoading(false));
			});
	};
}

export const fetchCategoryData = createAsyncThunk(
	'balance/fetchCategoryData',
	(props: { name: string; data: Record<string, any> }, { rejectWithValue }) =>
		ApiService.getInstance()
			.execute(props.name, props.data)
			.then((response) => {
				return response.data;
			})
			.catch((error) => {
				setErrorMessage(setError, {
					errorNumber: error?.response?.data?.error_number,
					statusCode: error?.response?.status,
				});
				rejectWithValue(error);
			})
);

export function getCurrenciesToDisplay(codes: any) {
	return async (dispatch: any) => {
		dispatch(setLoading(true));
		await ApiService.getInstance()
			.execute(apiFunctionsEnum.GET_CURRENCIES_TO_DISPLAY, { codes })
			.then((response: any) => {
				dispatch(setDashboardDisplayedCurrencies(response.data));
			})
			.catch((error: any) => {
				console.log(error);
				setErrorMessage(setError, {
					errorNumber: error?.response?.data?.error_number,
					statusCode: error?.response?.status,
				});
			})
			.finally(() => {
				dispatch(setLoading(false));
			});
	};
}

export function createRefund(refund: any) {
	return async (dispatch: any, state: any) => {
		dispatch(setLoading(true));
		return ApiService.getInstance()
			.execute(apiFunctionsEnum.CREATE_REFUND, refund)
			.then((response: any) => {
				const { balanceRefund } = response.data;
				if (refund.actionType && refund.actionType.toLowerCase() === 'payment' && balanceRefund.payment) {
					dispatch(getCompanyPaymentById(balanceRefund.companyId, balanceRefund.payment.id));
				}
				dispatch(setSuccessMessage(true));
				return response;
			})
			.catch((error: any) => {
				setErrorMessage(setError, {
					errorNumber: error?.response?.data?.error_number,
					statusCode: error?.response?.status,
				});
				return Promise.reject(error);
			})
			.finally(() => {
				dispatch(setLoading(false));
			});
	};
}

export const getDigitalBalanceRequests = createAsyncThunk(
	'balance/getDigitalBalanceRequests',
	(
		{
			documentedOnHyper,
			...params
		}: { documentedOnHyper: boolean; offset?: number; limit?: number; sortBy?: string; sortOrder?: string },
		{ rejectWithValue }
	) =>
		ApiService.getInstance()
			.execute(apiFunctionsEnum.GET_COMPLETED_DIGITAL_BALANCE_REQUESTS, {
				...params,
				filters: JSON.stringify({ documentedOnHyper }),
			})
			.then((response) => {
				return { documentedOnHyper, data: response.data };
			})
			.catch((err) => rejectWithValue(err))
);

export function editDigitalBalanceStatus(
	id: number,
	feezbackId: number,
	status: string,
	boUserId: number,
	boNotes: string
) {
	return async (dispatch: any) => {
		dispatch(setLoading(true));
		await ApiService.getInstance()
			.execute(apiFunctionsEnum.EDIT_DIGITAL_BALANCE_STATUS, {
				id,
				feezbackId,
				status,
				boUserId,
				boNotes,
			})
			.then(() => {
				dispatch(setSuccessMessage(true));
			})
			.catch((error: any) => {
				dispatch(
					setErrorMessage(setError, {
						errorNumber: error?.response?.data?.error_number,
						statusCode: error?.response?.status,
					})
				);
			})
			.finally(() => {
				dispatch(setLoading(false));
			});
	};
}
